import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { ConnectionTrendDayOption, TrendBucketMode } from '../lib/connectionDashboard';
import { persistDashboardTrendDays, readDashboardTrendDays } from '../lib/dashboardTrendDays';

export type DashboardChartId = 'network-reach' | 'engagements' | 'subscribers';

export type DashboardChartHoverState = {
  periodStart: string;
  dayCount: number;
  bucketMode: TrendBucketMode;
  sourceId: DashboardChartId;
};

type DashboardChartContextValue = {
  hover: DashboardChartHoverState | null;
  setHover: React.Dispatch<React.SetStateAction<DashboardChartHoverState | null>>;
  dayCount: ConnectionTrendDayOption;
  setDayCount: (days: ConnectionTrendDayOption) => void;
};

const DashboardChartContext = createContext<DashboardChartContextValue | null>(null);

export function DashboardChartProvider({ children }: { children: ReactNode }) {
  const [hover, setHover] = useState<DashboardChartHoverState | null>(null);
  const [dayCount, setDayCountState] = useState<ConnectionTrendDayOption>(() => readDashboardTrendDays());

  const setDayCount = useCallback((days: ConnectionTrendDayOption) => {
    setDayCountState(days);
    persistDashboardTrendDays(days);
    setHover(null);
  }, []);

  const value = useMemo(
    () => ({ hover, setHover, dayCount, setDayCount }),
    [hover, dayCount, setDayCount],
  );

  return <DashboardChartContext.Provider value={value}>{children}</DashboardChartContext.Provider>;
}

/** @deprecated Use DashboardChartProvider */
export const DashboardChartHoverProvider = DashboardChartProvider;

function useDashboardChartContext(): DashboardChartContextValue {
  const ctx = useContext(DashboardChartContext);
  if (!ctx) {
    throw new Error('useDashboardChartContext must be used within DashboardChartProvider');
  }
  return ctx;
}

export function useDashboardTrendDays() {
  const { dayCount, setDayCount } = useDashboardChartContext();
  return { dayCount, setDayCount };
}

type SyncedPoint = {
  periodStart: string;
  isFuture?: boolean;
};

export function useSyncedChartHover(
  chartId: DashboardChartId,
  dayCount: number,
  bucketMode: TrendBucketMode,
  points: SyncedPoint[],
) {
  const { hover, setHover } = useDashboardChartContext();
  const [localHoverIndex, setLocalHoverIndex] = useState<number | null>(null);

  const displayHoverIndex = useMemo(() => {
    if (hover?.sourceId === chartId) return localHoverIndex;
    if (!hover || hover.dayCount !== dayCount || hover.bucketMode !== bucketMode) return null;

    const idx = points.findIndex(
      (point) => point.periodStart === hover.periodStart && !point.isFuture,
    );
    return idx >= 0 ? idx : null;
  }, [hover, chartId, dayCount, bucketMode, localHoverIndex, points]);

  const isHoverSource = hover?.sourceId === chartId;

  const updateHoverAtIndex = useCallback(
    (index: number | null) => {
      setLocalHoverIndex(index);

      if (index == null) {
        setHover((current) => (current?.sourceId === chartId ? null : current));
        return;
      }

      const point = points[index];
      if (!point || point.isFuture) {
        setHover((current) => (current?.sourceId === chartId ? null : current));
        return;
      }

      setHover({ periodStart: point.periodStart, dayCount, bucketMode, sourceId: chartId });
    },
    [chartId, dayCount, bucketMode, points, setHover],
  );

  const clearHover = useCallback(() => {
    setLocalHoverIndex(null);
    setHover((current) => (current?.sourceId === chartId ? null : current));
  }, [chartId, setHover]);

  return {
    displayHoverIndex,
    updateHoverAtIndex,
    clearHover,
    isHoverSource,
  };
}
